import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Booking : famille "créneau daté".
 *
 * Famille du socle sur le modèle "famille + type" (cf. asset, le patron). Un
 * booking est un créneau daté qui engage un ou quelques tiers (contact,
 * intervenant) et des ressources précises (ressource, bien) : du simple
 * rendez-vous à la location ou au dépôt planifié. Le vocabulaire métier vit
 * dans `type` (chaîne libre déclarée par l'app : "appointment", "rental",
 * "dropoff"...), jamais dans le nom de collection.
 *
 * Les liens vers d'autres familles (contact, worker, resource, order) sont de
 * simples ids : aucune dépendance de schéma entre familles. Tous restent
 * optionnels (un créneau peut n'engager qu'une ressource) ; seul le
 * discriminant `type` est requis à la création.
 */

/** Champs métier de la famille booking (hors champs standard du socle). */
export const bookingCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "appointment", "rental"). */
  type: z.string().min(1),
  /** Fiche `contact` du tiers concerné (client, demandeur), optionnel. */
  contactId: z.string().min(1).optional(),
  /** Fiche `worker` de l'intervenant affecté au créneau, optionnel. */
  workerId: z.string().min(1).optional(),
  /** Ressources précises engagées (ids de fiches `resource`), optionnel. */
  resourceIds: z.array(z.string()).optional(),
  /** Début du créneau, optionnel. */
  startAt: z.date().optional(),
  /** Fin du créneau, optionnel. */
  endAt: z.date().optional(),
  /** État métier, chaîne libre pilotée par l'app (ex. "requested", "confirmed", "no_show"). */
  status: z.string().min(1).optional(),
  /** Canal d'origine, chaîne libre (ex. "online", "counter", "phone"), optionnel. */
  source: z.string().min(1).optional(),
  /** Commande `order` rattachée si le créneau est facturé, optionnel. */
  orderId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille booking (champs standard + champs métier). */
export const BookingSchema = z.object({
  ...baseEntityFields,
  ...bookingCoreFields,
});

/** Type d'un booking. */
export type Booking = z.infer<typeof BookingSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const BookingCreateInputSchema = z.object({
  ...bookingCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un booking. */
export type BookingCreateInput = z.infer<typeof BookingCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const BookingUpdateInputSchema = z
  .object({ ...bookingCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un booking. */
export type BookingUpdateInput = z.infer<typeof BookingUpdateInputSchema>;
